import React, { useEffect, useRef, useState } from "react";
import BdrCopyIcon from "../../../../components/BdrCopyIcon/BdrCopyIcon";
import copyStringToClipboard from "../../../../utils/formatters/copyStringToClipboard";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";

/**
 * A rail fact with TWO controls: the icon box COPIES the value, the body OPENS
 * something (the Messages thread, an email compose) — or is plain text when
 * there is nowhere to go, as for the address.
 *
 * TWO SIBLING BUTTONS, NEVER NESTED. The rows these replace were one whole-row
 * `<button>`; putting a copy button inside it would be a button inside a
 * button, which is invalid HTML and whose inner control does not reliably fire
 * (the same reason BdrDisclosure's trigger is a sibling of its action). The
 * row is a `div`; the icon and the body are each a real button, so keyboard
 * focus, Enter/Space and screen readers all work on both.
 *
 * The icon crossfades to a copy glyph on hover / focus and to a check for a
 * moment after a copy — that is `BdrCopyIcon`, shared with the Available
 * Sooner rows. The ROW is the hover host (hovering anywhere on the row shows
 * the affordance) and the copy button is a host too (keyboard focus). Nothing
 * moves — the rail is budgeted at ~40px a row and a layout shift on hover
 * would push every row under it.
 *
 * On touch there is no hover; the icon is still a button, so a tap on it copies
 * and a tap on the body opens. Self-discoverable either way.
 *
 * Snackbar through `handleSnack` only, and ONE call per click: it is a single
 * slot, so a "copied" after a "failed" in the same tick would overwrite it.
 */
const COPIED_MS = 1200;

/** "706 Mello Oak, San Antonio, Tx 78258" — what the address row copies. */
export const addressOneLine = ({ streetAddress, city, state, zipCode } = {}) => {
  const cityState = [city, state].filter(Boolean).join(", ");
  const cityLine = [cityState, zipCode].filter(Boolean).join(" ");
  return [streetAddress, cityLine].filter(Boolean).join(", ");
};

const ClientFactRow = ({
  /** the resting icon, e.g. <PhoneIcon /> */
  icon,
  /** the small caps label, e.g. "EMAIL" */
  label,
  /** the value, already formatted for display */
  children,
  /** what the clipboard receives */
  copyText,
  /** noun for the snackbar + aria-label: "phone number" | "email" | "address" */
  copyLabel,
  /** when present the body is a button that calls this */
  onOpen,
  /** accessible name for the body button */
  openLabel,
  /** optional hover title for the body button */
  title,
}) => {
  const { handleSnack } = useSnackbar();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = async () => {
    const ok = await copyStringToClipboard(copyText);
    if (!ok) {
      handleSnack(`Failed to copy ${copyLabel}`, 3000);
      return;
    }
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), COPIED_MS);
    handleSnack(`Copied ${copyLabel}`, 2000);
  };

  const body = (
    <>
      <span className="bdr-field-label">{label}</span>
      <span className="scc-fact-value">{children}</span>
    </>
  );

  return (
    <div className="scc-fact scc-fact--split bdr-copy-host">
      <button
        type="button"
        className="scc-fact-icon scc-fact-copy bdr-copy-host"
        aria-label={`Copy ${copyLabel}`}
        title={`Copy ${copyLabel}`}
        onClick={handleCopy}
      >
        <BdrCopyIcon icon={icon} copied={copied} />
      </button>

      {onOpen ? (
        <button
          type="button"
          className="scc-fact-body scc-fact-open"
          aria-label={openLabel}
          title={title}
          onClick={onOpen}
        >
          {body}
        </button>
      ) : (
        <span className="scc-fact-body">{body}</span>
      )}
    </div>
  );
};

export default ClientFactRow;
